import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Activity, Sun, Heart, Mic, Eye, Crown, Zap } from 'lucide-react';
import { PageHero, Section, Steps, Callout, Faq, ClosingCta } from '../components/content/Content';

// --- Data ---
const faqs = [
    { q: "What are chakras and where do they come from?", a: "Chakras are energy centers in the body, originating from ancient Indian Vedic traditions. The word 'chakra' means 'wheel' in Sanskrit, referring to spinning points of life force (prana)." },
    { q: "How do I know if my chakras are blocked?", a: "Blockages often manifest as physical stiffness, recurring emotional issues, or feeling stuck in specific areas of life (e.g., trouble speaking up might indicate a blocked Throat chakra)." },
    { q: "Can chakra healing be done remotely/online?", a: "Yes. Energy transcends physical space. Remote sessions use intention and visualization to clear and balance your field just as effectively as in-person work." },
    { q: "How does chakra healing complement Tarot reading?", a: "Tarot reveals the story and mental patterns, while chakra healing addresses the energetic root. Together, they offer a complete roadmap for transformation." },
    { q: "How many sessions do I need?", a: "It depends on your goals. A single session offers a reset, while a series allows for deep, layer-by-layer healing and long-term realignment." },
];

const chakraData = [
    { id: 'root', name: 'Root (Muladhara)', color: '#EF4444', icon: Activity, location: 'Base of spine', governs: 'Security, survival, grounding', balanced: 'Feeling safe, stable, present', blocked: 'Anxiety, fear, financial stress' },
    { id: 'sacral', name: 'Sacral (Svadhisthana)', color: '#F97316', icon: Zap, location: 'Lower abdomen', governs: 'Creativity, emotions, sexuality', balanced: 'Creative flow, emotional balance, pleasure', blocked: 'Emotional numbness, creative blocks, guilt' },
    { id: 'solar', name: 'Solar Plexus (Manipura)', color: '#EAB308', icon: Sun, location: 'Upper abdomen', governs: 'Personal power, confidence, willpower', balanced: 'Self-assured, motivated, decisive', blocked: 'Low self-esteem, lack of direction, powerlessness' },
    { id: 'heart', name: 'Heart (Anahata)', color: '#22C55E', icon: Heart, location: 'Center of chest', governs: 'Love, compassion, connection', balanced: 'Loving, empathetic, connected', blocked: 'Loneliness, bitterness, difficulty trusting' },
    { id: 'throat', name: 'Throat (Vishuddha)', color: '#3B82F6', icon: Mic, location: 'Throat', governs: 'Communication, truth, self-expression', balanced: 'Authentic, articulate, good listener', blocked: 'Fear of speaking up, miscommunication, secrets' },
    { id: 'third-eye', name: 'Third Eye (Ajna)', color: '#6366F1', icon: Eye, location: 'Forehead between eyebrows', governs: 'Intuition, wisdom, imagination', balanced: 'Clear intuition, insightful, imaginative', blocked: 'Confusion, lack of clarity, disconnected from intuition' },
    { id: 'crown', name: 'Crown (Sahasrara)', color: '#A855F7', icon: Crown, location: 'Top of head', governs: 'Spiritual connection, consciousness, enlightenment', balanced: 'Spiritually connected, purposeful, peaceful', blocked: 'Feeling disconnected, lack of purpose, cynicism' },
];

// --- Page Component ---
const AboutChakras: React.FC = () => {
    const reduce = useReducedMotion();
    return (
        <>
            <PageHero
                title="Discover your"
                accent="energy centres"
                subtitle="The seven chakras and their role in your spiritual, emotional and physical well-being."
            >
                {/* One dot per chakra, root to crown */}
                <div className="flex justify-center gap-3 mt-8" aria-hidden="true">
                    {chakraData.map((c, i) => (
                        <motion.span
                            key={c.id}
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: c.color }}
                            initial={reduce ? false : { y: 12, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                        />
                    ))}
                </div>
            </PageHero>

            <Section id="about" title="What are chakras?">
                <div className="max-w-3xl mx-auto">
                    <Callout>
                        <p className="font-fraunces text-xl text-plum leading-relaxed">
                            Chakras are spinning wheels of energy along your spine. When balanced, they support your physical health, emotional stability and spiritual growth.
                        </p>
                        <p className="mt-3">When blocked, they can show up as physical discomfort, emotional distress or a feeling of being stuck.</p>
                    </Callout>
                </div>
            </Section>

            <Section id="how" title="How chakra healing works" subtitle="Three steps to alignment" tint>
                <Steps
                    steps={[
                        { title: 'Assessment', body: 'Identify which chakras are blocked or imbalanced through guided conversation or energy sensing.' },
                        { title: 'Balancing', body: 'Use energy work, meditation, visualisation and sometimes Tarot to clear blockages.' },
                        { title: 'Integration', body: 'Receive practices and guidance to keep your chakras healthy in daily life.' },
                    ]}
                />
            </Section>

            <Section id="details" title="The seven chakras" subtitle="From root to crown">
                <ul className="grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
                    {chakraData.map((chakra) => (
                        <li key={chakra.id} className="border-l-2 pl-5" style={{ borderColor: chakra.color }}>
                            <div className="flex items-center gap-3 mb-1">
                                <chakra.icon className="w-5 h-5" style={{ color: chakra.color }} aria-hidden="true" />
                                <h3 className="font-fraunces text-xl">{chakra.name}</h3>
                            </div>
                            <p className="text-sm text-plum-soft italic mb-3">{chakra.location}</p>
                            <dl className="space-y-2 text-sm">
                                <div>
                                    <dt className="text-xs uppercase tracking-widest text-lavender-700 font-bold">Governs</dt>
                                    <dd className="text-plum">{chakra.governs}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-widest text-[#15803D] font-bold">Balanced</dt>
                                    <dd className="text-plum-soft">{chakra.balanced}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-widest text-[#9F1239] font-bold">Blocked</dt>
                                    <dd className="text-plum-soft">{chakra.blocked}</dd>
                                </div>
                            </dl>
                        </li>
                    ))}
                </ul>
            </Section>

            <Section id="faq" title="Questions" tint>
                <Faq items={faqs} />
            </Section>

            <ClosingCta source="about-chakras" />
        </>
    );
};

export default AboutChakras;
